import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import HeaderStats from '../components/HeaderStats';
import FilterSection from '../components/FilterSection';
import EdgeRankedGames from '../components/EdgeRankedGames';
import { useSport } from '../context/SportContext';
import { enrichPredictionsWithResults } from '../utils/api';
import { syncCurrentLine, CFB_LINE_KEYS } from '../utils/currentLine';
const CFBDashboard = () => {
    const { currentSport } = useSport();
    const [predictions, setPredictions] = useState(null);
    const [filteredGames, setFilteredGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        team: '',
        confidence: 'ALL',
        conference: 'ALL',
        week: 'ALL',
        date: 'ALL'
    });
    useEffect(() => {
        const loadPredictions = async () => {
            try {
                setLoading(true);
                const timestamp = Date.now();
                console.log('🏈 CFB: Fetching directly from static file with timestamp:', timestamp);
                const response = await fetch(`/static/cfb/cfb_predictions.json?v=${timestamp}&debug=true`, {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                });
                console.log('🏈 CFB: Response status:', response.status, response.statusText);
                if (!response.ok) {
                    throw new Error('Failed to fetch CFB predictions');
                }
                const data = await response.json();
                console.log('🏈 CFB: Raw data loaded:', {
                    keys: Object.keys(data),
                    hasPredictions: !!data.predictions,
                    predictionsCount: data.predictions?.length,
                    week: data.week,
                    season: data.season,
                    totalGames: data.total_games
                });
                const games = data.predictions
                    .map((raw) => syncCurrentLine(raw, CFB_LINE_KEYS))
                    .map((prediction) => ({
                    id: prediction.game_id,
                    home_team: prediction.home_team,
                    away_team: prediction.away_team,
                    week: data.week,
                    year: data.season,
                    game_date: prediction.game_date,
                    game_time: prediction.game_time,
                    venue: prediction.venue,
                    confidence_level: prediction.prediction?.confidence_tier || 'LOW',
                    team_to_cover: prediction.prediction?.pick_team,
                    display_confidence: (prediction.prediction?.pick_probability || 0.5) * 100,
                    spread_line: (() => {
                        try {
                            const pickTeam = prediction.prediction?.pick_team;
                            const homeTeam = prediction.home_team;
                            // Use the spread for the team that's actually picked
                            if (pickTeam === homeTeam) {
                                const spread = prediction.betting_analysis?.home_spread;
                                if (typeof spread === 'number')
                                    return spread;
                                if (typeof spread === 'string' && spread !== 'Odds not available') {
                                    const parsed = parseFloat(spread);
                                    return !isNaN(parsed) ? parsed : 0;
                                }
                                return 0;
                            }
                            else {
                                const spread = prediction.betting_analysis?.away_spread;
                                if (typeof spread === 'number')
                                    return spread;
                                if (typeof spread === 'string' && spread !== 'Odds not available') {
                                    const parsed = parseFloat(spread);
                                    return !isNaN(parsed) ? parsed : 0;
                                }
                                return 0;
                            }
                        }
                        catch (error) {
                            console.warn('Error processing spread_line:', error);
                            return 0;
                        }
                    })(),
                    // CFB-specific fields
                    home_covers: prediction.prediction?.home_covers,
                    pick_probability: prediction.prediction?.pick_probability,
                    confidence: prediction.prediction?.confidence,
                    betting_tier: prediction.betting_indicators?.betting_tier,
                    tier_reason: prediction.betting_indicators?.tier_reason,
                    bankroll_percentage: prediction.betting_indicators?.bankroll_percentage,
                    model_agreement_score: prediction.betting_indicators?.model_agreement_score,
                    unanimous_direction: prediction.betting_indicators?.unanimous_direction,
                    agreeing_models: prediction.betting_indicators?.agreeing_models,
                    edge_strength: prediction.betting_indicators?.edge_strength,
                    expected_roi_110: prediction.betting_indicators?.expected_roi_110,
                    expected_margin: prediction.betting_analysis?.expected_margin,
                    total_points: prediction.betting_analysis?.total_points,
                    home_moneyline: prediction.betting_analysis?.home_moneyline || -110,
                    away_moneyline: prediction.betting_analysis?.away_moneyline || -110,
                    home_spread_odds: prediction.betting_analysis?.home_spread_odds || -110,
                    away_spread_odds: prediction.betting_analysis?.away_spread_odds || -110,
                    // Pass through betting_indicators from prediction
                    betting_indicators: prediction.betting_indicators,
                    // Edge context ranking (drives the default sort order)
                    edge_rank: prediction.edge_rank,
                    edge_context: prediction.edge_context
                }));
                console.log('🏈 CFB: Transformed games:', {
                    gamesCount: games.length,
                    sampleGame: games[0],
                    sampleDisplayConfidence: games[0]?.display_confidence,
                    samplePickProbability: games[0]?.pick_probability,
                    first5Games: games.slice(0, 5).map(g => ({
                        id: g.id,
                        teams: `${g.away_team} @ ${g.home_team}`,
                        display_confidence: g.display_confidence,
                        pick_probability: g.pick_probability
                    }))
                });
                // Enrich with game results
                const dataForEnrichment = { ...data, games };
                const enrichedData = await enrichPredictionsWithResults(dataForEnrichment, 'cfb');
                console.log('🏈 CFB: Enriched with game results');
                setPredictions({ ...data, predictions: enrichedData.games });
                setFilteredGames(enrichedData.games);
            }
            catch (err) {
                setError('Failed to load CFB predictions. Please try again later.');
                console.error('🏈 CFB: Error loading predictions:', err);
            }
            finally {
                setLoading(false);
            }
        };
        loadPredictions();
    }, []);
    useEffect(() => {
        if (!predictions)
            return;
        // predictions.predictions now contains already-transformed and enriched games
        // No need to re-transform, just filter them
        let games = predictions.predictions;
        console.log('🏈 CFB: Starting filtering with games:', games.length);
        console.log('🏈 CFB: Sample game from predictions.predictions:', {
            sampleGame: games[0],
            display_confidence: games[0]?.display_confidence,
            pick_probability: games[0]?.pick_probability,
            first3: games.slice(0, 3).map(g => ({
                teams: `${g.away_team} @ ${g.home_team}`,
                display_confidence: g.display_confidence
            }))
        });
        // Apply team filter
        if (filters.team) {
            const teamLower = filters.team.toLowerCase();
            games = games.filter(game => game.home_team.toLowerCase().includes(teamLower) ||
                game.away_team.toLowerCase().includes(teamLower));
        }
        // Apply confidence filter
        if (filters.confidence !== 'ALL') {
            games = games.filter(game => game.confidence_level === filters.confidence);
        }
        // Apply week filter
        if (filters.week !== 'ALL') {
            games = games.filter(game => game.week === parseInt(filters.week));
        }
        // Apply date filter
        if (filters.date !== 'ALL') {
            games = games.filter(game => game.game_date === filters.date);
        }
        console.log('🏈 CFB: Filtered games:', games.length);
        console.log('🏈 CFB: Sample filtered game:', games[0]);
        console.log('🏈 CFB: Sample betting_indicators:', games[0]?.betting_indicators);
        console.log('🏈 CFB: Sample bet_ranking:', games[0]?.betting_indicators?.bet_ranking);
        setFilteredGames(games);
    }, [predictions, filters]);
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };
    if (loading) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "d-flex justify-content-center", children: _jsx("div", { className: "spinner-border", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) }) }) }));
    }
    if (error) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsxs("div", { className: "alert alert-danger", role: "alert", children: [_jsx("i", { className: "fas fa-exclamation-triangle me-2" }), error] }) }));
    }
    if (!predictions) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "alert alert-info", role: "alert", children: "No CFB predictions available at this time." }) }));
    }
    // Create metadata object from the root-level data
    const metadata = {
        prediction_date: predictions.prediction_date,
        sport: predictions.sport,
        week: predictions.week,
        season: predictions.season,
        total_games: predictions.total_games
    };
    return (_jsxs("div", { className: "container-mobile safe-area-padding", children: [_jsx(HeaderStats, { metadata: metadata, games: filteredGames, sport: currentSport }), _jsx(FilterSection, { filters: filters, onFilterChange: handleFilterChange, availableWeeks: [predictions.week], availableDates: [...new Set(predictions.predictions.map(g => g.game_date))].sort() }), _jsx(EdgeRankedGames, { games: filteredGames })] }));
};
export default CFBDashboard;
